import React from 'react';
import { MapPin, RotateCw, Bot, Megaphone } from 'lucide-react';
import { Message } from '../../../types/models/whatsapp';
import MessageStatusIcon from './MessageStatusIcon';
import MediaContent from './MediaContent';

interface MessageBubbleProps {
  message: Message;
  onRetry?: (messageId: string) => void;
}

const formatTime = (timestamp: number): string =>
  new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

/**
 * Renders one message across all supported types and statuses.
 *
 * Highest-traffic component in the product — it must stay cheap to render, so
 * it holds no state and derives everything from props.
 */
const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onRetry }) => {
  const isOutbound = message.direction === 'outbound';
  const isFailed = message.status === 'failed';

  // System messages are centered notices, not bubbles.
  if (message.type === 'system') {
    return (
      <div className="my-2 flex justify-center">
        <span className="rounded-full bg-gray-200 px-3 py-1 text-xs text-gray-600">
          {message.system?.body}
        </span>
      </div>
    );
  }

  const bubbleTone = isOutbound
    ? 'bg-wa-bubbleOut'
    : 'bg-wa-bubbleIn';

  const renderContent = () => {
    switch (message.type) {
      case 'text':
        return (
          <p className="whitespace-pre-wrap break-words text-[15px] leading-snug">
            {message.text?.body}
          </p>
        );

      case 'image':
      case 'video':
      case 'audio':
      case 'document':
      case 'sticker':
        return message.media ? (
          <MediaContent type={message.type} media={message.media} />
        ) : null;

      case 'location':
        return (
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 shrink-0 text-danger-500" />
            <span className="text-sm">
              {message.location?.name || 'Shared location'}
              {message.location?.address && (
                <span className="block text-xs text-gray-500">
                  {message.location.address}
                </span>
              )}
            </span>
          </div>
        );

      case 'template':
        return (
          <div className="space-y-1">
            <span className="inline-block rounded bg-black/5 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
              Template · {message.template?.name}
            </span>
            <p className="whitespace-pre-wrap break-words text-[15px] leading-snug">
              {message.text?.body || ''}
            </p>
          </div>
        );

      case 'interactive':
        return (
          <div className="space-y-2">
            {message.interactive?.body && (
              <p className="text-[15px] leading-snug">{message.interactive.body}</p>
            )}
            {message.interactive?.selectedTitle && (
              <span className="inline-block rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-600">
                Replied: {message.interactive.selectedTitle}
              </span>
            )}
            {message.interactive?.buttons?.map((button) => (
              <span
                key={button.id}
                className="block rounded border border-gray-200 px-2 py-1 text-center text-xs text-primary-600"
              >
                {button.title}
              </span>
            ))}
          </div>
        );

      case 'reaction':
        return (
          <span className="text-2xl" aria-label="Reaction">
            {message.reaction?.emoji}
          </span>
        );

      default:
        return (
          <p className="text-sm italic text-gray-500">
            Unsupported message type: {message.type}
          </p>
        );
    }
  };

  return (
    <div
      className={`flex w-full ${isOutbound ? 'justify-end' : 'justify-start'} px-4 py-0.5`}
    >
      <div className={`flex max-w-[75%] flex-col ${isOutbound ? 'items-end' : 'items-start'}`}>
        <div
          className={`rounded-2xl px-3 py-2 shadow-sm ${bubbleTone} ${
            isFailed ? 'ring-1 ring-danger-400' : ''
          }`}
        >
          {renderContent()}

          <div className="mt-1 flex items-center justify-end gap-1">
            {message.automationRunId && (
              <Bot className="h-3 w-3 text-gray-400" aria-label="Sent by automation" />
            )}
            <span className="text-[10px] text-gray-500">
              {formatTime(message.createdAt)}
            </span>
            {isOutbound && <MessageStatusIcon status={message.status} />}
          </div>
        </div>

        {isFailed && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs text-danger-600">
              {message.error?.title || 'Failed to send'}
            </span>
            {onRetry && (
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                <RotateCw className="h-3 w-3" />
                Retry
              </button>
            )}
          </div>
        )}

        {/* Attribution: campaign first, then bot, then agent — an agent
            reading the thread needs to know a broadcast wrote this, not a
            colleague they could ask about it. */}
        {isOutbound && message.broadcastId && (
          <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-gray-400">
            <Megaphone className="h-2.5 w-2.5" />
            {message.broadcastName || 'Campaign'}
          </span>
        )}

        {isOutbound &&
          message.sentByAgentName &&
          !message.automationRunId &&
          !message.broadcastId && (
            <span className="mt-0.5 text-[10px] text-gray-400">
              {message.sentByAgentName}
            </span>
          )}
      </div>
    </div>
  );
};

export default React.memo(MessageBubble);
